
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */
import {
  FormGroup,
  Label,
  Input,
  InputGroup,
  Checkbox,
  Select,
  Margin,
  Size,
  Hint,
  Flex,
  FlexItem,
  FlexJustify,
  Category,
} from '@drawbotics/react-drylus';
import Playground from '~/components/Playground';


const layoutProps = {
  
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


<h1>{`Form Group`}</h1>
<p>{`This component sets the layout when combining a `}<inlineCode parentName="p">{`Label`}</inlineCode>{` with a form element or multiple form elements (see `}<a parentName="p" {...{
        "href": "/component-kit/forms/input-group"
      }}><inlineCode parentName="a">{`InputGroup`}</inlineCode></a>{` for more examples).
Usage`}</p>
<pre><code parentName="pre" {...{
        "className": "language-jsx"
      }}>{`import { FormGroup } from '@drawbotics/react-drylus';
`}</code></pre>
<p><strong parentName="p">{`Example`}</strong></p>
<Playground __position={0} __code={`<FormGroup
  input={<Input placeholder="Something..." value="" onChange={x=>x} />}
  label={<Label>My input label</Label>} />`} component={FormGroup} mdxType="Playground">
  <FormGroup input={<Input placeholder="Something..." value="" onChange={x => x} mdxType="Input" />} label={<Label mdxType="Label">My input label</Label>} mdxType="FormGroup" />
</Playground>
<h3>{`InputGroup example`}</h3>
<p>{`As mentioned above you can set the `}<inlineCode parentName="p">{`FormGroup`}</inlineCode>{` to take multiple components as inputs, in order to "group" inputs into a single label.`}</p>
<Playground __position={1} __code={`<FormGroup
  input={
    <InputGroup hint="This is a global hint">
      <Checkbox checked>First one</Checkbox>
      <Checkbox>Second one</Checkbox>
      <Checkbox>Third one</Checkbox>
    </InputGroup>
  }
  label={<Label>My group of inputs</Label>} />`} mdxType="Playground">
  <FormGroup input={<InputGroup hint="This is a global hint" mdxType="InputGroup">
        <Checkbox checked mdxType="Checkbox">First one</Checkbox>
        <Checkbox mdxType="Checkbox">Second one</Checkbox>
        <Checkbox mdxType="Checkbox">Third one</Checkbox>
      </InputGroup>} label={<Label mdxType="Label">My group of inputs</Label>} mdxType="FormGroup" />
</Playground>
<p>{`And can be horizontal too of course and have a global error or validation:`}</p>
<Playground __position={2} __code={`<>
  <FormGroup
    horizontal
    input={
      <InputGroup error="This is a global error">
        <Checkbox checked>First one</Checkbox>
        <Checkbox>Second one</Checkbox>
        <Checkbox>Third one</Checkbox>
      </InputGroup>
    }
    label={<Label>My group of checkboxes</Label>} />
  <Margin size={{ bottom: Size.DEFAULT }} />
  <FormGroup
    horizontal
    input={
      <InputGroup valid>
        <Input value="Something" onChange={x=>x} />
        <Input value="Something else" onChange={x=>x} />
      </InputGroup>
    }
    label={<Label>My group of inputs</Label>} />
</>`} mdxType="Playground">
  <>
    <FormGroup horizontal input={<InputGroup error="This is a global error" mdxType="InputGroup">
          <Checkbox checked mdxType="Checkbox">First one</Checkbox>
          <Checkbox mdxType="Checkbox">Second one</Checkbox>
          <Checkbox mdxType="Checkbox">Third one</Checkbox>
        </InputGroup>} label={<Label mdxType="Label">My group of checkboxes</Label>} mdxType="FormGroup" />
    <Margin size={{
          bottom: Size.DEFAULT
        }} mdxType="Margin" />
    <FormGroup horizontal input={<InputGroup valid mdxType="InputGroup">
          <Input value="Something" onChange={x => x} mdxType="Input" />
          <Input value="Something else" onChange={x => x} mdxType="Input" />
        </InputGroup>} label={<Label mdxType="Label">My group of inputs</Label>} mdxType="FormGroup" />
  </>
</Playground>
<h3>{`Address example`}</h3>
<p>{`Sometimes you might need to create complex form groups, for example for an address section. `}<strong parentName="p">{`Note`}</strong>{` you will have to set the layout and display of the hint/error yourself when making this kind of form groups, same goes for the error states of the components. See `}<a parentName="p" {...{
        "href": "/component-kit/forms/hint"
      }}><inlineCode parentName="a">{`Hint`}</inlineCode></a>{` documentation for that.`}</p>
<Playground __position={3} __code={`<FormGroup
  input={
    <>
      <Flex justify={FlexJustify.START} wrap>
        <FlexItem flex={3/4}>
          <Margin size={{ right: Size.EXTRA_SMALL, bottom: Size.EXTRA_SMALL }}>
            <Input valid placeholder="Street & Number" value="1 Real Avenue" onChange={x=>x} />
          </Margin>
        </FlexItem>
        <FlexItem flex={1/4}>
          <Margin size={{ bottom: Size.EXTRA_SMALL }}>
            <Input error placeholder="ZIP" onChange={x=>x} value="" />
          </Margin>
        </FlexItem>
        <FlexItem flex={1/2}>
          <Margin size={{ right: Size.EXTRA_SMALL }}>
            <Input valid placeholder="City" onChange={x=>x} value="Brussels" />
          </Margin>
        </FlexItem>
        <FlexItem flex={1/2}>
          <Select error placeholder="Country" options={[{ value: 'IT', label: 'Italy' }, { value: 'FR', label: 'France' }]} onChange={x=>x} />
        </FlexItem>
      </Flex>
      <Hint category={Category.DANGER}>All fields are required</Hint>
    </>
  }
  label={<Label>Address of residence</Label>} />`} mdxType="Playground">
  <FormGroup input={<>
        <Flex justify={FlexJustify.START} wrap mdxType="Flex">
          <FlexItem flex={3 / 4} mdxType="FlexItem">
            <Margin size={{
              right: Size.EXTRA_SMALL,
              bottom: Size.EXTRA_SMALL
            }} mdxType="Margin">
              <Input valid placeholder="Street & Number" value="1 Real Avenue" onChange={x => x} mdxType="Input" />
            </Margin>
          </FlexItem>
          <FlexItem flex={1 / 4} mdxType="FlexItem">
            <Margin size={{
              bottom: Size.EXTRA_SMALL
            }} mdxType="Margin">
              <Input error placeholder="ZIP" onChange={x => x} value="" mdxType="Input" />
            </Margin>
          </FlexItem>
          <FlexItem flex={1 / 2} mdxType="FlexItem">
            <Margin size={{
              right: Size.EXTRA_SMALL
            }} mdxType="Margin">
              <Input valid placeholder="City" onChange={x => x} value="Brussels" mdxType="Input" />
            </Margin>
          </FlexItem>
          <FlexItem flex={1 / 2} mdxType="FlexItem">
            <Select error placeholder="Country" options={[{
              value: 'IT',
              label: 'Italy'
            }, {
              value: 'FR',
              label: 'France'
            }]} onChange={x => x} mdxType="Select" />
          </FlexItem>
        </Flex>
        <Hint category={Category.DANGER} mdxType="Hint">All fields are required</Hint>
      </>} label={<Label mdxType="Label">Address of residence</Label>} mdxType="FormGroup" />
</Playground>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;